import cityData from "./city-data.json";

export interface CityPageData {
  city: string;
  slug: string;
  country: string;
  countrySlug: string;
  countryCode: string;
  path: string;
  pageTitle: string;
  heroTitle: string;
  heroDescription: string;
  description: string;
  intro: string;
  outcomes: string[];
  features: string[];
  process: Array<{ title: string; description: string }>;
  serviceTypes: string[];
  contentSections: Array<{ heading: string; body: string }>;
  faqs: Array<{ question: string; answer: string }>;
  seoDescription: string;
  keywords: string[];
}

type TemplateCity = {
  city: string;
  slug: string;
  country: string;
  countrySlug: string;
  countryCode: string;
  path: string;
};

function applyCityTemplate(value: string, cityInfo: TemplateCity) {
  return value
    .replace(/\{city\}/g, cityInfo.city)
    .replace(/\{cityLower\}/g, cityInfo.city.toLowerCase())
    .replace(/\{country\}/g, cityInfo.country)
    .replace(/\{countryLower\}/g, cityInfo.country.toLowerCase())
    .replace(/\{countrySlug\}/g, cityInfo.countrySlug)
    .replace(/\{countryCode\}/g, cityInfo.countryCode);
}

export const cityPages: CityPageData[] = cityData.cities.map((cityInfo) => ({
  ...cityInfo,
  pageTitle: applyCityTemplate(cityData.templates.pageTitle, cityInfo),
  heroTitle: applyCityTemplate(cityData.templates.heroTitle, cityInfo),
  heroDescription: applyCityTemplate(cityData.templates.heroDescription, cityInfo),
  description: applyCityTemplate(cityData.templates.description, cityInfo),
  intro: applyCityTemplate(cityData.templates.intro, cityInfo),
  outcomes: cityData.outcomes.map((item) => applyCityTemplate(item, cityInfo)),
  features: cityData.features.map((item) => applyCityTemplate(item, cityInfo)),
  process: cityData.process.map((step) => ({
    title: applyCityTemplate(step.title, cityInfo),
    description: applyCityTemplate(step.description, cityInfo),
  })),
  serviceTypes: cityData.serviceTypes.map((item) =>
    applyCityTemplate(item, cityInfo),
  ),
  contentSections: cityData.contentSections.map((section) => ({
    heading: applyCityTemplate(section.heading, cityInfo),
    body: applyCityTemplate(section.body, cityInfo),
  })),
  faqs: cityData.faqs.map((faq) => ({
    question: applyCityTemplate(faq.question, cityInfo),
    answer: applyCityTemplate(faq.answer, cityInfo),
  })),
  seoDescription: applyCityTemplate(cityData.templates.seoDescription, cityInfo),
  keywords: cityData.templates.keywords.map((keyword) =>
    applyCityTemplate(keyword, cityInfo),
  ),
}));

const featuredCitySlugs = new Set([
  "london",
  "manchester",
  "oxford",
  "cambridge",
  "boston",
  "new-york",
  "san-francisco",
  "los-angeles",
  "toronto",
  "montreal",
  "vancouver",
  "waterloo",
  "melbourne",
  "sydney",
  "brisbane",
  "canberra",
  "berlin",
  "munich",
  "heidelberg",
  "aachen",
]);

function groupCityPages(pages: CityPageData[]) {
  return pages.reduce<
    Array<{
      country: string;
      countrySlug: string;
      countryCode: string;
      cities: CityPageData[];
    }>
  >((groups, page) => {
    const existingGroup = groups.find(
      (group) => group.countrySlug === page.countrySlug,
    );

    if (existingGroup) {
      existingGroup.cities.push(page);
      return groups;
    }

    groups.push({
      country: page.country,
      countrySlug: page.countrySlug,
      countryCode: page.countryCode,
      cities: [page],
    });

    return groups;
  }, []);
}

export const countryCityGroups = groupCityPages(cityPages);

export const featuredCountryCityGroups = groupCityPages(
  cityPages.filter((page) => featuredCitySlugs.has(page.slug)),
);

export function getCityPageBySlug(slug?: string, countrySlug?: string) {
  const normalizedSlug = (slug || "").replace(/^assignment-help-/, "");
  const normalizedCountrySlug = countrySlug?.toLowerCase();

  return cityPages.find((page) => {
    if (page.slug !== normalizedSlug) {
      return false;
    }

    return normalizedCountrySlug
      ? page.countrySlug === normalizedCountrySlug
      : true;
  });
}
